const RANGE_YEARS = { '1Y': 1, '3Y': 3, '5Y': 5 };
const dateValue = (value) => new Date(`${value}T00:00:00Z`).getTime();

export const getSvgPointInPlot = ({
    svg,
    plot,
    pointX,
    pointY,
    viewBoxWidth,
    viewBoxHeight,
}) => {
    const plotRect = plot.getBoundingClientRect();
    const scrollLeft = plot.scrollLeft || 0;
    const scrollTop = plot.scrollTop || 0;
    const toPlotCoordinates = (screenPoint) => ({
        x: screenPoint.x - plotRect.left + scrollLeft,
        y: screenPoint.y - plotRect.top + scrollTop,
    });

    if (typeof svg.createSVGPoint === 'function' && typeof svg.getScreenCTM === 'function') {
        try {
            const matrix = svg.getScreenCTM();
            if (matrix) {
                const point = svg.createSVGPoint();
                point.x = pointX;
                point.y = pointY;
                const screenPoint = point.matrixTransform(matrix);
                if (Number.isFinite(screenPoint.x) && Number.isFinite(screenPoint.y)) {
                    return toPlotCoordinates(screenPoint);
                }
            }
        } catch {
            // Deterministically fall back when SVG matrix APIs are incomplete.
        }
    }

    const svgRect = svg.getBoundingClientRect();
    const scale = Math.min(svgRect.width / viewBoxWidth, svgRect.height / viewBoxHeight);
    const offsetX = (svgRect.width - viewBoxWidth * scale) / 2;
    const offsetY = (svgRect.height - viewBoxHeight * scale) / 2;
    return toPlotCoordinates({
        x: svgRect.left + offsetX + pointX * scale,
        y: svgRect.top + offsetY + pointY * scale,
    });
};

const normalizeEciPoint = (row) => {
    if (!Number.isFinite(row.eci) || !row.eciDate || !row.eciModelName || !row.eciConfidence) return null;
    return {
        ...row,
        releaseDate: row.releaseDate || row.date,
        releaseModel: row.releaseModel || row.model,
        date: row.eciDate,
        model: row.eciModelName,
        plotDate: row.eciDate,
    };
};

const hasComparableEci = (row) => Number.isFinite(row.eci);

export const selectHighestTierReleases = (series = []) => {
    const selectedByGroup = new Map();

    series.forEach((row, index) => {
        const group = row.versionGroup || `ungrouped:${index}`;
        const current = selectedByGroup.get(group);
        if (!current) {
            selectedByGroup.set(group, row);
            return;
        }

        const currentHasEci = hasComparableEci(current);
        const rowHasEci = hasComparableEci(row);
        const hasHigherEci = rowHasEci && (!currentHasEci || row.eci > current.eci);
        const breaksTieLater = rowHasEci === currentHasEci
            && (!rowHasEci || row.eci === current.eci)
            && row.date > current.date;
        if (hasHigherEci || breaksTieLater) selectedByGroup.set(group, row);
    });

    return [...selectedByGroup.values()]
        .sort((a, b) => a.date.localeCompare(b.date) || a.model.localeCompare(b.model));
};

export const deriveEciFrontier = (series = []) => {
    let runningMax = Number.NEGATIVE_INFINITY;
    return selectHighestTierReleases(series)
        .map(normalizeEciPoint)
        .filter(Boolean)
        .sort((a, b) => a.date.localeCompare(b.date) || a.model.localeCompare(b.model))
        .map((row) => {
            const isFrontier = row.eci > runningMax;
            runningMax = Math.max(runningMax, row.eci);
            return { ...row, isFrontier, frontierValue: runningMax, isCarryIn: false };
        });
};

export const selectReleaseWindow = (series = [], range = 'All', asOf) => {
    const rawSorted = [...series].sort((a, b) => a.date.localeCompare(b.date) || a.model.localeCompare(b.model));
    const domainEnd = asOf || rawSorted.at(-1)?.date;
    if (!domainEnd) return { releases: [], scored: [], domainStart: null, domainEnd: null };

    let domainStart = rawSorted[0]?.date;
    if (RANGE_YEARS[range]) {
        const start = new Date(`${domainEnd}T00:00:00Z`);
        start.setUTCFullYear(start.getUTCFullYear() - RANGE_YEARS[range]);
        domainStart = start.toISOString().slice(0, 10);
    }

    const eligibleSeries = series.filter((row) => (
        row.date <= domainEnd && (!row.eciDate || row.eciDate <= domainEnd)
    ));
    const sorted = selectHighestTierReleases(eligibleSeries);
    const allScored = deriveEciFrontier(sorted);
    const frontierByRelease = new Map(allScored.map((row) => [
        `${row.releaseDate}\u0000${row.releaseModel}`,
        row.isFrontier,
    ]));
    const releases = sorted
        .filter((row) => row.date >= domainStart && row.date <= domainEnd)
        .map((row) => {
            const key = `${row.date}\u0000${row.model}`;
            return frontierByRelease.has(key)
                ? { ...row, isFrontier: frontierByRelease.get(key) }
                : row;
        });
    const inRangeScored = allScored.filter((row) => row.date >= domainStart);
    const priorFrontier = allScored.filter((row) => row.date < domainStart && row.isFrontier).at(-1);
    const scored = priorFrontier
        ? [{ ...priorFrontier, plotDate: domainStart, isCarryIn: true }, ...inRangeScored]
        : inRangeScored;

    return { releases, scored, domainStart, domainEnd };
};

export const assignReleaseRows = (points, minGap = 104, rowCount = 3, labelLayout = null) => {
    const lastLabelX = Array(rowCount).fill(Number.NEGATIVE_INFINITY);
    const lastLabelEnd = Array(rowCount).fill(Number.NEGATIVE_INFINITY);
    const lastPointX = Array(rowCount).fill(Number.NEGATIVE_INFINITY);

    return points.map((point) => {
        const hasMeasuredLabel = Boolean(labelLayout && Number.isFinite(point.labelWidth));
        const labelPadding = labelLayout?.padding ?? 7;
        const plotRight = labelLayout?.right ?? Number.POSITIVE_INFINITY;
        const placeLabelLeft = hasMeasuredLabel && point.x + labelPadding + point.labelWidth > plotRight;
        const labelX = point.x + (placeLabelLeft ? -labelPadding : labelPadding);
        const labelStart = placeLabelLeft ? labelX - point.labelWidth : labelX;
        const labelEnd = placeLabelLeft ? labelX : labelX + (point.labelWidth || 0);
        let row = hasMeasuredLabel
            ? lastLabelEnd.findIndex((value) => labelStart - value >= minGap)
            : lastLabelX.findIndex((value) => point.x - value >= minGap);
        const showLabel = row !== -1;
        if (!showLabel) row = lastPointX.indexOf(Math.min(...lastPointX));
        if (showLabel) {
            lastLabelX[row] = point.x;
            lastLabelEnd[row] = labelEnd;
        }
        lastPointX[row] = point.x;
        return {
            ...point,
            row,
            showLabel,
            labelX,
            labelAnchor: placeLabelLeft ? 'end' : 'start',
        };
    });
};

export const buildStaircasePath = (points, x, y) => points.reduce((path, point, index) => {
    const pointX = x(point);
    const pointY = y(point.frontierValue);
    if (!index) return `M ${pointX} ${pointY}`;
    const previousY = y(points[index - 1].frontierValue);
    return `${path} L ${pointX} ${previousY} L ${pointX} ${pointY}`;
}, '');

export const getDisplayModel = (row) => row.displayModel || row.releaseModel || row.model;

export const getReleaseAriaLabel = (row, layer, isFrontier, isZh = false) => {
    const score = Number.isFinite(row.eci)
        ? `ECI ${row.eci.toFixed(1)}，${isFrontier ? '刷新能力前沿' : '未刷新能力前沿'}`
        : 'Epoch 暂无可比 ECI';
    const localizedScore = isZh ? score : Number.isFinite(row.eci)
        ? `ECI ${row.eci.toFixed(1)}, ${isFrontier ? 'new capability frontier' : 'below capability frontier'}`
        : 'No comparable Epoch ECI';
    return `${row.date}, ${getDisplayModel(row)}, ${row.organization}, ${localizedScore}, ${layer === 'eci' ? 'ECI point' : 'release record'}`;
};

export const getFrontierSummary = (rows, isZh = false) => {
    const latest = deriveEciFrontier(rows).filter((row) => row.isFrontier).at(-1);
    if (!latest) return isZh ? '当前范围没有可比 ECI 数据。' : 'No comparable ECI data in this range.';
    return isZh
        ? `最新能力前沿为 ${latest.eci.toFixed(1)} ECI，由 ${getDisplayModel(latest)} 于 ${latest.date} 创下。`
        : `The latest capability frontier is ${latest.eci.toFixed(1)} ECI, set by ${getDisplayModel(latest)} on ${latest.date}.`;
};

export { dateValue };
